import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatPKR } from './currencyFormatter';
import { addPdfBrandLogo } from './pdfLogo';

function pickProduct(row) {
  return row.product || {};
}

function pickVariant(row) {
  return row.variant || {};
}

function pickVendor(p) {
  return p.vendor || {};
}

function variantLabel(row) {
  const v = pickVariant(row);
  const c = String(v.color ?? '').trim();
  const s = String(v.size ?? '').trim();
  if (c && s) return `${c} / ${s}`;
  if (c) return c;
  if (s) return s;
  return '—';
}

function formatQty(q, unitType) {
  const n = parseFloat(q);
  if (Number.isNaN(n)) return '0';
  const u = String(unitType || '').toLowerCase();
  if (u === 'guz') return n.toFixed(2);
  return String(Math.round(n));
}

function unitCost(row) {
  const p = pickProduct(row);
  const v = pickVariant(row);
  const vc = parseFloat(v.cost_price ?? v.costPrice);
  if (!Number.isNaN(vc) && vc > 0) return vc;
  const pc = parseFloat(p.cost_price ?? p.costPrice);
  return Number.isNaN(pc) ? 0 : pc;
}

function displayType(p) {
  const t = (p.product_type ?? p.productType ?? '').toString();
  if (t === 'stitched') return 'Stitched';
  if (t === 'unstitched') return 'Unstitched';
  return t || '—';
}

function displayUnit(p) {
  const u = (p.unit_type ?? p.unitType ?? '').toString().toLowerCase();
  if (u === 'guz') return 'Guz';
  if (u === 'piece') return 'Piece';
  return u || '—';
}

function isActiveInventoryRow(row) {
  const p = pickProduct(row);
  const v = pickVariant(row);
  const productId = p.id ?? row.product_id ?? row.productId;
  const variantId = v.id ?? row.variant_id ?? row.variantId;
  return Boolean(productId && (p.name || p.product_code || p.productCode) && variantId);
}

/**
 * Full branch stock report (variant lines, vendor-style layout).
 *
 * @param {{ businessName: string, branchName: string, inventory: object[] }} options
 */
export async function downloadStockReportPdf(options) {
  const { businessName, branchName, inventory } = options;
  const list = (Array.isArray(inventory) ? inventory : []).filter(isActiveInventoryRow);

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 10;
  const { nextY } = await addPdfBrandLogo(doc, { maxWidthMm: 28, startY: 6, gapAfterMm: 3 });
  let y = nextY;

  doc.setFontSize(15);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text('Full stock report', margin, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(55, 65, 81);
  doc.text(String(businessName || 'Business').trim(), margin, y);
  y += 5;
  doc.text(`Branch: ${String(branchName || '—').trim()}`, margin, y);
  y += 5;
  const generated = new Date();
  doc.text(`Generated: ${generated.toLocaleString()}`, margin, y);
  y += 5;
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.text(
    'Quantities are per variant in this branch. Stock value uses variant cost if set, otherwise product cost.',
    margin,
    y
  );
  y += 6;

  let totalValue = 0;
  const body = list.map((row) => {
    const p = pickProduct(row);
    const vendor = pickVendor(p);
    const qty = parseFloat(row.quantity) || 0;
    const uc = unitCost(row);
    const lineVal = qty * uc;
    totalValue += lineVal;
    const code = p.product_code ?? p.productCode ?? '—';
    const name = (p.name || '—').toString();
    return [
      String(code).substring(0, 14),
      name.substring(0, 36),
      displayType(p),
      (vendor.name || '—').toString().substring(0, 22),
      variantLabel(row).substring(0, 24),
      displayUnit(p),
      formatQty(qty, p.unit_type ?? p.unitType),
      formatPKR(uc),
      formatPKR(lineVal)
    ];
  });

  body.push([
    '',
    '',
    '',
    '',
    '',
    '',
    'Total (at cost)',
    '',
    formatPKR(totalValue)
  ]);

  autoTable(doc, {
    startY: y,
    head: [['Code', 'Product', 'Type', 'Vendor', 'Variant (color / size)', 'Unit', 'Qty', 'Unit cost', 'Stock value']],
    body,
    margin: { left: margin, right: margin },
    tableWidth: pageW - 2 * margin,
    styles: { fontSize: 7, cellPadding: 1.4, textColor: [15, 23, 42], lineColor: [200, 200, 200], lineWidth: 0.1 },
    headStyles: { fillColor: [55, 65, 81], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7 },
    theme: 'grid',
    didParseCell: (data) => {
      const isTotal = data.section === 'body' && data.row.index === body.length - 1;
      if (isTotal) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [248, 250, 252];
      }
    },
    columnStyles: {
      6: { halign: 'right' },
      7: { halign: 'right' },
      8: { halign: 'right' }
    }
  });

  const safeBranch = String(branchName || 'branch')
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 32);
  const dateStr = generated.toISOString().split('T')[0];
  doc.save(`stock-report-${safeBranch}-${dateStr}.pdf`);
}
